/**
 * Shopify Cart Module (Storefront API)
 * Handles cart operations with buyerIdentity for continuous authentication from cart to checkout
 * This ensures logged-in customers remain authenticated during checkout and see their saved addresses
 */

class ShopifyCart {
  constructor(apiClient) {
    this.api = apiClient;
    this.utils = window.ShopifyUtils;
  }

  /**
   * Storage keys for cart data
   */
  STORAGE_KEYS = {
    CART_ID: 'shopifyStorefrontCartId',
    CHECKOUT_URL: 'shopifyStorefrontCheckoutUrl',
    CART_TOKEN: 'cartToken'
  };

  /**
   * Create a new cart with optional buyer identity
   */
  CART_CREATE_MUTATION = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          totalQuantity
          buyerIdentity {
            email
            customer {
              id
              email
              firstName
              lastName
            }
          }
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                    }
                  }
                }
                attributes {
                  key
                  value
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  /**
   * Get existing cart by ID
   */
  CART_QUERY = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        totalQuantity
        buyerIdentity {
          email
          customer {
            id
            email
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
    }
  `;

  /**
   * Update buyer identity on existing cart
   */
  CART_BUYER_IDENTITY_UPDATE_MUTATION = `
    mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
        cart {
          id
          checkoutUrl
          buyerIdentity {
            email
            customer {
              id
              email
              firstName
              lastName
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  /**
   * Add lines to cart
   */
  CART_LINES_ADD_MUTATION = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          totalQuantity
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                    }
                  }
                }
                attributes {
                  key
                  value
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  /**
   * Update lines in cart
   */
  CART_LINES_UPDATE_MUTATION = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          totalQuantity
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                  }
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  /**
   * Remove lines from cart
   */
  CART_LINES_REMOVE_MUTATION = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          checkoutUrl
          totalQuantity
          lines(first: 100) {
            edges {
              node {
                id
                quantity
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  /**
   * Get stored cart ID
   * @returns {string|null}
   */
  getStoredCartId() {
    return localStorage.getItem(this.STORAGE_KEYS.CART_ID);
  }

  /**
   * Store cart ID
   * @param {string} cartId
   */
  setStoredCartId(cartId) {
    localStorage.setItem(this.STORAGE_KEYS.CART_ID, cartId);
  }

  /**
   * Clear stored cart data
   */
  clearStoredCart() {
    localStorage.removeItem(this.STORAGE_KEYS.CART_ID);
    localStorage.removeItem(this.STORAGE_KEYS.CHECKOUT_URL);
    localStorage.removeItem(this.STORAGE_KEYS.CART_TOKEN);
  }

  /**
   * Get stored checkout URL
   * @returns {string|null}
   */
  getStoredCheckoutUrl() {
    return localStorage.getItem(this.STORAGE_KEYS.CHECKOUT_URL);
  }

  /**
   * Store checkout URL
   * @param {string} url
   */
  setStoredCheckoutUrl(url) {
    localStorage.setItem(this.STORAGE_KEYS.CHECKOUT_URL, url);
  }

  /**
   * Create a new cart
   * @param {Object} options - Cart creation options
   * @param {Array} options.lines - Line items to add
   * @param {string} options.customerAccessToken - Customer access token for authenticated cart
   * @returns {Promise<Object>}
   */
  async createCart(options = {}) {
    try {
      const input = {};

      // Add buyer identity if customer is logged in
      if (options.customerAccessToken) {
        input.buyerIdentity = {
          customerAccessToken: options.customerAccessToken
        };
      }

      // Add lines if provided
      if (options.lines && options.lines.length > 0) {
        input.lines = options.lines;
      }

      const variables = { input };
      const response = await this.api.mutate(this.CART_CREATE_MUTATION, variables);

      if (response.data?.cartCreate?.userErrors?.length > 0) {
        const error = response.data.cartCreate.userErrors[0];
        throw new Error(error.message);
      }

      const cart = response.data?.cartCreate?.cart;
      if (cart) {
        // Store cart ID and checkout URL
        this.setStoredCartId(cart.id);
        if (cart.checkoutUrl) {
          this.setStoredCheckoutUrl(cart.checkoutUrl);
        }

        return {
          success: true,
          cart: this.processCartData(cart)
        };
      }

      throw new Error('Failed to create cart');
    } catch (error) {
      console.error('Error creating cart:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get cart by ID
   * @param {string} cartId - Cart ID
   * @returns {Promise<Object>}
   */
  async getCart(cartId = null) {
    try {
      const id = cartId || this.getStoredCartId();
      if (!id) {
        return {
          success: false,
          error: 'No cart ID found'
        };
      }

      const variables = { cartId: id };
      const response = await this.api.query(this.CART_QUERY, variables);

      const cart = response.data?.cart;
      if (cart) {
        // Update stored checkout URL
        if (cart.checkoutUrl) {
          this.setStoredCheckoutUrl(cart.checkoutUrl);
        }

        return {
          success: true,
          cart: this.processCartData(cart)
        };
      }

      // Cart not found - clear stored data
      this.clearStoredCart();
      return {
        success: false,
        error: 'Cart not found'
      };
    } catch (error) {
      console.error('Error getting cart:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update buyer identity on cart (for authentication sync)
   * @param {string} customerAccessToken - Customer access token
   * @param {string} cartId - Optional cart ID (uses stored ID if not provided)
   * @returns {Promise<Object>}
   */
  async updateBuyerIdentity(customerAccessToken, cartId = null) {
    try {
      const id = cartId || this.getStoredCartId();
      if (!id) {
        return {
          success: false,
          error: 'No cart ID found'
        };
      }

      const variables = {
        cartId: id,
        buyerIdentity: {
          customerAccessToken: customerAccessToken
        }
      };

      const response = await this.api.mutate(this.CART_BUYER_IDENTITY_UPDATE_MUTATION, variables);

      if (response.data?.cartBuyerIdentityUpdate?.userErrors?.length > 0) {
        const error = response.data.cartBuyerIdentityUpdate.userErrors[0];
        throw new Error(error.message);
      }

      const cart = response.data?.cartBuyerIdentityUpdate?.cart;
      if (cart) {
        // Update stored checkout URL (important - this URL now includes auth)
        if (cart.checkoutUrl) {
          this.setStoredCheckoutUrl(cart.checkoutUrl);
        }

        return {
          success: true,
          cart: {
            id: cart.id,
            checkoutUrl: cart.checkoutUrl,
            buyerIdentity: cart.buyerIdentity
          }
        };
      }

      throw new Error('Failed to update buyer identity');
    } catch (error) {
      console.error('Error updating buyer identity:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Add lines to cart
   * @param {Array} lines - Line items to add
   * @param {string} cartId - Optional cart ID
   * @returns {Promise<Object>}
   */
  async addLines(lines, cartId = null) {
    try {
      const id = cartId || this.getStoredCartId();

      // If no cart exists, create one first
      if (!id) {
        const customerToken = this.utils?.getCustomerToken?.();
        const createResult = await this.createCart({
          lines: lines,
          customerAccessToken: customerToken
        });
        return createResult;
      }

      const variables = {
        cartId: id,
        lines: lines
      };

      const response = await this.api.mutate(this.CART_LINES_ADD_MUTATION, variables);

      if (response.data?.cartLinesAdd?.userErrors?.length > 0) {
        const error = response.data.cartLinesAdd.userErrors[0];
        throw new Error(error.message);
      }

      const cart = response.data?.cartLinesAdd?.cart;
      if (cart) {
        if (cart.checkoutUrl) {
          this.setStoredCheckoutUrl(cart.checkoutUrl);
        }

        return {
          success: true,
          cart: this.processCartData(cart)
        };
      }

      throw new Error('Failed to add lines to cart');
    } catch (error) {
      console.error('Error adding lines to cart:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update lines in cart
   * @param {Array} lines - Line items to update (with id and quantity)
   * @param {string} cartId - Optional cart ID
   * @returns {Promise<Object>}
   */
  async updateLines(lines, cartId = null) {
    try {
      const id = cartId || this.getStoredCartId();
      if (!id) {
        return {
          success: false,
          error: 'No cart ID found'
        };
      }

      const variables = {
        cartId: id,
        lines: lines
      };

      const response = await this.api.mutate(this.CART_LINES_UPDATE_MUTATION, variables);

      if (response.data?.cartLinesUpdate?.userErrors?.length > 0) {
        const error = response.data.cartLinesUpdate.userErrors[0];
        throw new Error(error.message);
      }

      const cart = response.data?.cartLinesUpdate?.cart;
      if (cart) {
        if (cart.checkoutUrl) {
          this.setStoredCheckoutUrl(cart.checkoutUrl);
        }

        return {
          success: true,
          cart: this.processCartData(cart)
        };
      }

      throw new Error('Failed to update cart lines');
    } catch (error) {
      console.error('Error updating cart lines:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Remove lines from cart
   * @param {Array} lineIds - Line IDs to remove
   * @param {string} cartId - Optional cart ID
   * @returns {Promise<Object>}
   */
  async removeLines(lineIds, cartId = null) {
    try {
      const id = cartId || this.getStoredCartId();
      if (!id) {
        return {
          success: false,
          error: 'No cart ID found'
        };
      }

      const variables = {
        cartId: id,
        lineIds: lineIds
      };

      const response = await this.api.mutate(this.CART_LINES_REMOVE_MUTATION, variables);

      if (response.data?.cartLinesRemove?.userErrors?.length > 0) {
        const error = response.data.cartLinesRemove.userErrors[0];
        throw new Error(error.message);
      }

      const cart = response.data?.cartLinesRemove?.cart;
      if (cart) {
        if (cart.checkoutUrl) {
          this.setStoredCheckoutUrl(cart.checkoutUrl);
        }

        return {
          success: true,
          cart: this.processCartData(cart)
        };
      }

      throw new Error('Failed to remove cart lines');
    } catch (error) {
      console.error('Error removing cart lines:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get the authenticated checkout URL
   * ALWAYS fetches a fresh checkout URL by updating buyer identity
   * Per Shopify best practices: "request the checkoutUrl when the buyer is ready to proceed to checkout"
   * @returns {Promise<string|null>}
   */
  async getAuthenticatedCheckoutUrl() {
    try {
      const customerToken = this.utils?.getCustomerToken?.();
      const cartId = this.getStoredCartId();

      // If no cart ID exists, we cannot get a fresh checkout URL
      if (!cartId) {
        console.warn('No cart ID found for checkout');
        // Only use stored URL as absolute last resort
        const storedUrl = this.getStoredCheckoutUrl();
        if (storedUrl) {
          return storedUrl;
        }
        return null;
      }

      // ALWAYS try to get a fresh checkout URL by updating buyer identity
      // This is Shopify's recommended approach for security and reliability
      if (customerToken) {
        try {
          const result = await this.updateBuyerIdentity(customerToken);
          if (result.success && result.cart?.checkoutUrl) {
            return result.cart.checkoutUrl;
          }
        } catch (identityError) {
          console.warn('Failed to update buyer identity:', identityError);
          // Continue to try fetching cart directly
        }
      }

      // If buyer identity update failed or no token, try to fetch cart directly for checkout URL
      try {
        const cartResult = await this.getCart(cartId);
        if (cartResult.success && cartResult.cart?.checkoutUrl) {
          return cartResult.cart.checkoutUrl;
        }
      } catch (cartError) {
        console.warn('Failed to fetch cart:', cartError);
      }

      // Only use stored URL as absolute last resort
      const storedUrl = this.getStoredCheckoutUrl();
      if (storedUrl) {
        console.warn('Using stored checkout URL as last resort');
        return storedUrl;
      }

      return null;
    } catch (error) {
      console.error('Error getting authenticated checkout URL:', error);
      // Last resort fallback
      return this.getStoredCheckoutUrl();
    }
  }

  /**
   * Process cart data for easier consumption
   * @param {Object} cart - Raw cart data from GraphQL
   * @returns {Object}
   */
  processCartData(cart) {
    const lines = cart.lines?.edges?.map(edge => ({
      id: edge.node.id,
      quantity: edge.node.quantity,
      merchandise: edge.node.merchandise,
      attributes: edge.node.attributes || []
    })) || [];

    return {
      id: cart.id,
      checkoutUrl: cart.checkoutUrl,
      totalQuantity: cart.totalQuantity,
      buyerIdentity: cart.buyerIdentity,
      lines: lines,
      cost: cart.cost
    };
  }

  /**
   * Convert variant ID to GID format for Storefront API
   * @param {string|number} variantId - Variant ID (numeric or GID)
   * @returns {string}
   */
  toVariantGid(variantId) {
    if (typeof variantId === 'string' && variantId.startsWith('gid://')) {
      return variantId;
    }
    return `gid://shopify/ProductVariant/${variantId}`;
  }

  /**
   * Create line input for adding to cart
   * @param {string|number} variantId - Product variant ID
   * @param {number} quantity - Quantity
   * @param {Array} attributes - Optional attributes (key/value pairs)
   * @returns {Object}
   */
  createLineInput(variantId, quantity = 1, attributes = []) {
    const line = {
      merchandiseId: this.toVariantGid(variantId),
      quantity: quantity
    };

    if (attributes && attributes.length > 0) {
      line.attributes = attributes;
    }

    return line;
  }

  /**
   * Sync cart with customer authentication state
   * Call this after login to associate cart with customer
   * @returns {Promise<Object>}
   */
  async syncWithCustomerAuth() {
    const customerToken = this.utils?.getCustomerToken?.();
    const cartId = this.getStoredCartId();

    if (!customerToken) {
      // Not logged in - nothing to sync
      return { success: true, message: 'No customer session to sync' };
    }

    if (!cartId) {
      // No cart exists - create one with buyer identity
      return await this.createCart({ customerAccessToken: customerToken });
    }

    // Update existing cart's buyer identity
    return await this.updateBuyerIdentity(customerToken);
  }

  /**
   * Handle logout - optionally clear cart association
   * @param {boolean} clearCart - Whether to clear the cart entirely
   */
  handleLogout(clearCart = false) {
    if (clearCart) {
      this.clearStoredCart();
    }
    // Note: The cart will remain but buyer identity will be cleared on next operation
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.ShopifyCart = ShopifyCart;
}

